import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useDashboardData } from "@/hooks/useDashboardData";
import DashboardHeader from "@/components/dashboard/DashboardHeader";
import DashboardFooter from "@/components/dashboard/DashboardFooter";
import DashboardIntro from "@/components/dashboard/DashboardIntro";
import MapContainer from "@/components/dashboard/MapContainer";
import CustomerContribution from "@/components/dashboard/CustomerContribution";
import { fetchProjectVideos, ProjectVideo } from "@/services/api";
import ProjectVideoSection from "@/components/dashboard/ProjectVideoSection";
import { motion } from "framer-motion";
import CategoryOverview from "@/components/dashboard/CategoryOverview";
import DashboardTirolerRohreIntro from "@/components/dashboard/DashboardTirolerRohreIntro";
import { TIROLER_ROHRE_CUSTOMER_ID } from "@/constants/customerIds";

const Dashboard = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const accessId = searchParams.get("accessId");
  const { t } = useLanguage();

  const {
    customer,
    projectsDetails,
    isLoading,
    isLoadingBatchData,
    isError,
    selectedProject,
    batchTitles,
    mapCenter,
    handleProjectSelect,
    handleCloseProjectPanel,
    projectToBatchIds,
    batchPlannedInvestments,
    batchMeasures,
  } = useDashboardData(accessId || undefined);

  const [projectVideos, setProjectVideos] = useState<
    Record<string, ProjectVideo[]>
  >({});

  useEffect(() => {
    document.title = customer
      ? `${customer.friendlyName || customer.name} | ${t("dashboard.title")}`
      : t("dashboard.title");
  }, [customer, t]);

  const uniqueIssuers = React.useMemo(() => {
    if (!projectsDetails) return [];

    const uniqueLogoUrls = new Set();
    return projectsDetails.reduce(
      (acc, project) => {
        const issuer = project.defaultIssuer;
        if (issuer?.logoUrl && !uniqueLogoUrls.has(issuer.logoUrl)) {
          uniqueLogoUrls.add(issuer.logoUrl);
          acc.push(issuer);
        }
        return acc;
      },
      [] as Array<{ name: string; logoUrl?: string }>,
    );
  }, [projectsDetails]);

  // Calculate total contribution from customer transactions
  const totalContribution = React.useMemo(() => {
    if (!customer?.account?.toTransactions) return 0;

    return customer.account.toTransactions.reduce((sum, transaction) => {
      return sum + (transaction.amount || 0);
    }, 0);
  }, [customer]);

  useEffect(() => {
    const fetchVideosForProjects = async () => {
      if (!projectsDetails?.length) return;

      const videosMap: Record<string, ProjectVideo[]> = {};

      try {
        await Promise.all(
          projectsDetails.map(async (project) => {
            const videos = await fetchProjectVideos(project.id);
            if (videos.length > 0) {
              videosMap[project.id] = videos;
            }
          }),
        );

        setProjectVideos(videosMap);
      } catch (error) {
        console.error("Error fetching project videos:", error);
      }
    };

    fetchVideosForProjects();
  }, [projectsDetails]);

  if (isLoading) {
    return (
      <div className="px-4 md:px-6 lg:px-8 max-w-[1400px] mx-auto py-8">
        <div className="h-96 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-lg font-medium">{t("dashboard.loading")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !customer) {
    return (
      <div className="px-4 md:px-6 lg:px-8 max-w-[1400px] mx-auto py-8">
        <div className="h-96 flex items-center justify-center">
          <div className="text-center">
            <Info className="h-8 w-8 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">
              {t("dashboard.error")}
            </h2>
            <p className="text-muted-foreground mb-4">
              {t("dashboard.errorDesc")}
            </p>
            <Link to="/">
              <Button>{t("dashboard.returnHome")}</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const customerColor = customer?.primaryColor || "#00383c";

  return (
    <TooltipProvider>
      <DashboardHeader
        customerLogo={customer?.logoUrl}
        customerName={customer?.friendlyName ?? customer?.name}
        customerColor={customerColor}
        projectDeveloper={projectsDetails?.[0]?.projectDeveloper}
      />

      <div className="bg-gray-50">
        <div className="px-4 md:px-6 lg:px-8 max-w-[1400px] mx-auto py-8">
          {/* Show a different intro for Tiroler Rohre */}
          {customer.id === TIROLER_ROHRE_CUSTOMER_ID ? (
            <DashboardTirolerRohreIntro
              customerId={customer.id}
              customerColor={customerColor}
              projectDeveloperName={
                projectsDetails?.[0]?.projectDeveloper.friendlyName ??
                projectsDetails?.[0]?.projectDeveloper.name
              }
              customerName={customer.friendlyName ?? customer.name}
              uniqueIssuers={uniqueIssuers}
              totalContribution={totalContribution}
              projects={projectsDetails}
              projectToBatchIds={projectToBatchIds}
              batchTitles={batchTitles}
            />
          ) : (
            <DashboardIntro
              customerId={customer.id}
              customerColor={customerColor}
              projectDeveloperName={
                projectsDetails?.[0]?.projectDeveloper.friendlyName ??
                projectsDetails?.[0]?.projectDeveloper.name
              }
              customerName={customer.friendlyName ?? customer.name}
              uniqueIssuers={uniqueIssuers}
              totalContribution={totalContribution}
              projects={projectsDetails}
              projectToBatchIds={projectToBatchIds}
              batchTitles={batchTitles}
            />
          )}

          <div className="mx-auto" id="supported-projects">
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <MapContainer
                mapCenter={mapCenter}
                projects={projectsDetails || []}
                selectedProject={selectedProject}
                projectToBatchIds={projectToBatchIds}
                batchTitles={batchTitles}
                onSelectProject={handleProjectSelect}
                onCloseProjectPanel={handleCloseProjectPanel}
                customerColor={customerColor}
                batchPlannedInvestments={batchPlannedInvestments}
                batchMeasures={batchMeasures}
                isLoadingBatchData={isLoadingBatchData}
              />
            </motion.div>

            {projectsDetails.length > 1 && (
              <motion.div
                initial={{ opacity: 0, y: 100 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="mt-32"
              >
                <CustomerContribution
                  transactions={customer.account.toTransactions}
                  projectsDetails={projectsDetails || []}
                  customerColor={customerColor}
                />
              </motion.div>
            )}

            <motion.div
              initial={{ opacity: 0, y: 100 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="mt-32"
            >
              <CategoryOverview
                projects={projectsDetails || []}
                projectToBatchIds={projectToBatchIds}
                batchTitles={batchTitles}
                customerColor={customerColor}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 100 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="mt-32"
            >
              <ProjectVideoSection
                videos={projectVideos}
                customerColor={customerColor}
              />
            </motion.div>
          </div>
        </div>
      </div>

      <DashboardFooter />
    </TooltipProvider>
  );
};

export default Dashboard;
